/* Help Center — search runtime
 * Lunr-free, dependency-free fuzzy search over a static manifest.
 * The manifest is fetched on first use from the URL in the data-src of
 * <script id="help-search-manifest" type="application/json"> (an inline JSON
 * body, if present, is read instead), then kept in sessionStorage.
 *
 * Manifest shape:
 *   [{ id, sectionId, title, summary, category, categoryUrl, url, body }]
 *
 * Strategy:
 *  - Tokenize query → array of normalized tokens (lowercased, accents stripped).
 *  - Score each article: title match weight 8, summary 3, category 2, body 1.
 *  - Whole-token > prefix > substring; titles get an extra boost on whole-token.
 *  - Return top N (default 8) sorted by score desc.
 *  - Live-filter at 80ms debounce. Up/Down arrow + Enter for keyboard nav.
 *  - URL deep-link: /pages/help-center?q=foundation runs the search on load.
 */
(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn, { once: true });
  }

  function normalize(s) {
    if (!s) return '';
    return String(s)
      .toLowerCase()
      .normalize('NFKD').replace(/[̀-ͯ]/g, '')   // strip diacritics
      .replace(/[^a-z0-9\s\-_/.]/g, ' ')                    // drop punctuation
      .replace(/\s+/g, ' ')
      .trim();
  }

  function tokenize(s) {
    var n = normalize(s);
    if (!n) return [];
    return n.split(' ').filter(function (t) { return t.length > 1; });
  }

  function scoreField(haystack, tokens, fieldWeight, boostWholeWord) {
    if (!haystack) return 0;
    var h = normalize(haystack);
    if (!h) return 0;
    var score = 0;
    for (var i = 0; i < tokens.length; i++) {
      var t = tokens[i];
      // Whole token
      var rxWord = new RegExp('(^|\\s|/|\\.|-)' + escapeRx(t) + '($|\\s|/|\\.|-)');
      if (rxWord.test(h)) {
        score += fieldWeight * (boostWholeWord ? 1.6 : 1.0);
        continue;
      }
      // Prefix
      var rxPrefix = new RegExp('(^|\\s|/|\\.|-)' + escapeRx(t));
      if (rxPrefix.test(h)) { score += fieldWeight * 0.7; continue; }
      // Substring
      if (h.indexOf(t) !== -1) { score += fieldWeight * 0.4; }
    }
    return score;
  }

  function escapeRx(s) { return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }

  function debounce(fn, ms) {
    var t = null;
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, ms);
    };
  }

  function highlight(text, tokens) {
    if (!text) return '';
    var safe = String(text).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    if (!tokens.length) return safe;
    tokens.forEach(function (t) {
      try {
        var rx = new RegExp('(' + escapeRx(t) + ')', 'ig');
        safe = safe.replace(rx, '<mark>$1</mark>');
      } catch (e) { /* skip bad token */ }
    });
    return safe;
  }

  function snippet(body, tokens, len) {
    if (!body) return '';
    if (!tokens.length) return body.slice(0, len) + (body.length > len ? '…' : '');
    var lower = body.toLowerCase();
    var idx = -1;
    for (var i = 0; i < tokens.length; i++) {
      var t = tokens[i].toLowerCase();
      idx = lower.indexOf(t);
      if (idx !== -1) break;
    }
    if (idx === -1) return body.slice(0, len) + (body.length > len ? '…' : '');
    var start = Math.max(0, idx - 40);
    var end = Math.min(body.length, idx + len - 40);
    return (start > 0 ? '…' : '') + body.slice(start, end) + (end < body.length ? '…' : '');
  }

  var manifest = null;
  var manifestRequest = null;

  // The returns category page was renamed (store pages say "returns", never
  // "trial"); the index still files its seven articles under the old label
  // and address. Show them under the page they live on now.
  var RENAMED = { '/pages/help-returns-365-day-trial': { url: '/pages/help-returns-365-day-returns', label: '365-day Returns' } };
  function fixCategories(list) {
    for (var i = 0; i < list.length; i++) {
      var to = list[i] && RENAMED[list[i].categoryUrl];
      if (to) { list[i].categoryUrl = to.url; list[i].category = to.label; }
    }
    return list;
  }
  var STORE_KEY = 'bettr:help-search-index';

  // Resolves with the article index: inline JSON if the page carries it,
  // else this session's stored copy, else one fetch of data-src.
  function loadManifest() {
    if (manifest) return Promise.resolve(manifest);
    if (manifestRequest) return manifestRequest;
    var el = document.getElementById('help-search-manifest');
    if (!el) return Promise.resolve(manifest = []);
    var inline = (el.textContent || '').trim();
    if (inline) {
      try { manifest = fixCategories(JSON.parse(inline)); } catch (e) { manifest = []; }
      return Promise.resolve(manifest);
    }
    try {
      var stored = sessionStorage.getItem(STORE_KEY);
      if (stored) { manifest = fixCategories(JSON.parse(stored)); return Promise.resolve(manifest); }
    } catch (e) {}
    var src = el.getAttribute('data-src');
    if (!src) return Promise.resolve(manifest = []);
    manifestRequest = fetch(src, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : []; })
      .then(function (data) {
        manifest = fixCategories(Array.isArray(data) ? data : []);
        if (manifest.length) {
          try { sessionStorage.setItem(STORE_KEY, JSON.stringify(manifest)); } catch (e) {}
        }
        return manifest;
      })
      // A failed fetch leaves an empty index (the "No matches, browse all
      // categories" state) rather than re-fetching on every keystroke.
      .catch(function () { return (manifest = []); });
    return manifestRequest;
  }

  function init() {
    var inputs = document.querySelectorAll('[data-help-search-input]');
    if (!inputs.length) return;

    inputs.forEach(function (input) {
      var form = input.closest('[data-help-search-form]');
      var resultsEl = form && form.parentElement ? form.parentElement.querySelector('[data-help-search-results]') : null;
      var clearBtn = form && form.querySelector('[data-help-search-clear]');
      if (!form || !resultsEl) return;

      var activeIndex = -1;
      var lastResults = [];

      function setActive(state) {
        form.classList.toggle('is-active', state);
        input.setAttribute('aria-expanded', state ? 'true' : 'false');
      }

      function render(results, tokens) {
        if (!tokens.length) {
          resultsEl.classList.remove('is-open');
          resultsEl.innerHTML = '';
          return;
        }
        if (!results.length) {
          resultsEl.classList.add('is-open');
          resultsEl.innerHTML = '<p class="custom-help-hero__results-empty">No matches. Try different words, or <a href="/pages/help-center">browse all categories</a>.</p>';
          return;
        }
        var html = '';
        for (var i = 0; i < results.length; i++) {
          var r = results[i];
          html += '<a class="custom-help-hero__result" href="' + r.url + '" data-index="' + i + '">' +
                  (r.category ? '<p class="custom-help-hero__result-cat">' + highlight(r.category, []) + '</p>' : '') +
                  '<p class="custom-help-hero__result-title">' + highlight(r.title, tokens) + '</p>' +
                  (r.summary ? '<p class="custom-help-hero__result-snippet">' + highlight(snippet(r.summary, tokens, 140), tokens) + '</p>' : '') +
                  '</a>';
        }
        resultsEl.innerHTML = html;
        resultsEl.classList.add('is-open');
        activeIndex = -1;
      }

      function search(q) {
        var tokens = tokenize(q);
        if (!tokens.length) { render([], []); lastResults = []; return; }
        // First search on this page: fetch the index, then run the latest query.
        if (!manifest) { loadManifest().then(function () { search(input.value); }); return; }

        var hits = [];
        for (var i = 0; i < manifest.length; i++) {
          var item = manifest[i];
          var s = 0;
          s += scoreField(item.title, tokens, 8, true);
          s += scoreField(item.summary, tokens, 3, false);
          s += scoreField(item.category, tokens, 2, false);
          s += scoreField(item.body, tokens, 1, false);
          if (item.sectionId && tokens.indexOf(item.sectionId.toLowerCase()) !== -1) s += 12;
          if (s > 0) hits.push({ score: s, item: item });
        }
        hits.sort(function (a, b) { return b.score - a.score; });
        var top = hits.slice(0, 8).map(function (h) { return h.item; });
        lastResults = top;
        render(top, tokens);
      }

      var debounced = debounce(function () { search(input.value); }, 80);

      input.addEventListener('input', function () {
        setActive(input.value.length > 0);
        debounced();
      });

      input.addEventListener('focus', function () {
        loadManifest();  // warm the index while the visitor starts typing
        if (input.value.length > 0) {
          setActive(true);
          debounced();
        }
      });

      input.addEventListener('keydown', function (e) {
        if (!resultsEl.classList.contains('is-open')) return;
        var resultEls = resultsEl.querySelectorAll('.custom-help-hero__result');
        if (!resultEls.length) return;

        if (e.key === 'ArrowDown') {
          e.preventDefault();
          activeIndex = (activeIndex + 1) % resultEls.length;
          resultEls.forEach(function (el, i) { el.classList.toggle('is-active', i === activeIndex); });
          resultEls[activeIndex].scrollIntoView({ block: 'nearest' });
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          activeIndex = (activeIndex - 1 + resultEls.length) % resultEls.length;
          resultEls.forEach(function (el, i) { el.classList.toggle('is-active', i === activeIndex); });
          resultEls[activeIndex].scrollIntoView({ block: 'nearest' });
        } else if (e.key === 'Enter') {
          if (activeIndex >= 0 && resultEls[activeIndex]) {
            e.preventDefault();
            window.location.href = resultEls[activeIndex].getAttribute('href');
          }
          // else let form submit naturally → /pages/help-center?q=...
        } else if (e.key === 'Escape') {
          resultsEl.classList.remove('is-open');
          input.blur();
        }
      });

      if (clearBtn) {
        clearBtn.addEventListener('click', function () {
          input.value = '';
          setActive(false);
          render([], []);
          input.focus();
        });
      }

      // Click-outside dismisses the panel. Named handler + section:unload
      // teardown so the document-level reference doesn't outlive the section
      // across Theme Editor reloads. Input/clearBtn listeners are bound to
      // nodes that get removed with the section, so they don't need teardown.
      function onDocClick(e) {
        if (!form.contains(e.target) && !resultsEl.contains(e.target)) {
          resultsEl.classList.remove('is-open');
        }
      }
      document.addEventListener('click', onDocClick);

      document.addEventListener('shopify:section:unload', function onUnload(e) {
        if (e.target && e.target.contains(form)) {
          document.removeEventListener('click', onDocClick);
          document.removeEventListener('shopify:section:unload', onUnload);
        }
      });

      // URL deep-link: ?q=...
      var params = new URLSearchParams(window.location.search);
      var initial = params.get('q');
      if (initial) {
        input.value = initial;
        setActive(true);
        search(initial);
      }
    });
  }

  // Plain-text cross-references in an article ("see Sections 2.1 through
  // 2.4") were dead ends: the content links single refs but not ranges or
  // pairs. Link every section number in such a phrase to its article, using
  // the same index. Only articles that contain an unlinked reference fetch it.
  function linkSectionRefs() {
    var body = document.querySelector('.custom-help-art__body');
    if (!body || !window.NodeFilter) return;
    var phrase = /Sections?\s+\d{1,2}\.\d{1,2}(?:\s*(?:,|and|or|to|through)\s*\d{1,2}\.\d{1,2})*/g;
    var walker = document.createTreeWalker(body, NodeFilter.SHOW_TEXT, {
      acceptNode: function (n) {
        var el = n.parentElement;
        if (!el || el.closest('a, h1, .custom-help-art__meta, script, style')) return NodeFilter.FILTER_REJECT;
        phrase.lastIndex = 0;
        return phrase.test(n.nodeValue) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
      }
    });
    var nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    if (!nodes.length) return;
    loadManifest().then(function (items) {
      var urlFor = {};
      items.forEach(function (it) { if (it && it.sectionId && it.url) urlFor[it.sectionId] = it.url; });
      nodes.forEach(function (node) {
        var text = node.nodeValue, frag = document.createDocumentFragment(), last = 0, m;
        phrase.lastIndex = 0;
        while ((m = phrase.exec(text))) {
          frag.appendChild(document.createTextNode(text.slice(last, m.index)));
          m[0].split(/(\d{1,2}\.\d{1,2})/).forEach(function (part) {
            var url = urlFor[part];
            if (url && url !== window.location.pathname) {
              var a = document.createElement('a');
              a.href = url;
              a.textContent = part;
              frag.appendChild(a);
            } else if (part) {
              frag.appendChild(document.createTextNode(part));
            }
          });
          last = m.index + m[0].length;
        }
        frag.appendChild(document.createTextNode(text.slice(last)));
        node.parentNode.replaceChild(frag, node);
      });
    });
  }

  ready(init);
  ready(function () {
    if ('requestIdleCallback' in window) window.requestIdleCallback(linkSectionRefs, { timeout: 3000 });
    else setTimeout(linkSectionRefs, 1500);
  });
})();
